/* eslint-disable react/prop-types */
import React, { useRef } from 'react'
import * as d3 from 'd3'
import StrikeZoneDumb from './Dumb.tsx'

import type { EVPData } from '../../../schema/types.ts'

const defaultStrokeWidth = 4

function useId() {
    const idRef = useRef(`id-${Math.random().toString(36).substring(2, 9)}`)
    return idRef.current
}

type StrikeZoneFuzzyProps = {
    xScale: d3.ScaleLinear<number, number>
    yScale: d3.ScaleLinear<number, number>
    data: EVPData[]
}

export default function StrikeZoneFuzzy({
    xScale,
    yScale,
    data,
}: StrikeZoneFuzzyProps) {
    const [topMin, topMax] = d3.extent(data, (d) => d.szTop)
    const [botMin, botMax] = d3.extent(data, (d) => d.szBot)

    if (topMin === undefined || topMax === undefined) {
        console.warn('No data or no strike zone data')
        return null
    }
    if (botMin === undefined || botMax === undefined) {
        console.warn('No data or no strike zone data')
        return null
    }

    // We will assume that we never have a state where top and bottom are equal but width and depth are different.
    // Fair assumption?
    if (topMin === topMax && botMin === botMax) {
        const szWidth = data[0].szWidth || 17
        return (
            <StrikeZoneDumb
                xScale={xScale}
                yScale={yScale}
                szTop={topMin}
                szBot={botMin}
                plateWidthFt={szWidth / 12}
            />
        )
    }

    const height = yScale(botMax) - yScale(topMin)

    const topHeight = yScale(topMin) - yScale(topMax)
    const bottomHeight = yScale(botMin) - yScale(botMax)
    const fullWidth = xScale(17 / 2 / 12) - xScale(-17 / 2 / 12)

    const patternId = useId()

    return (
        <g id="strikeZone">
            <defs>
                <pattern
                    id={patternId}
                    patternUnits="userSpaceOnUse"
                    width="4"
                    height="4"
                >
                    <path
                        id="background-ball"
                        d="M-1,3 L3,-1
               M1,5 L5,1"
                        style={{
                            stroke: 'rgb(78, 121, 167)',
                            strokeWidth: '10px',
                        }}
                    />
                    <path
                        id="hatches-ball"
                        d="M-2,2 L2,-2
               M0,4 L4,0
               M2,6 L6,2"
                        style={{ stroke: 'white', strokeWidth: 1 }}
                    />
                </pattern>
            </defs>
            {/* Top */}
            <rect
                height={topHeight}
                width={fullWidth}
                fill={`url(#${patternId})`}
                fillOpacity={0.6}
                x={xScale(-17 / 2 / 12)}
                y={yScale(topMax)}
                strokeWidth={0}
            />

            {/* Bottom */}
            <rect
                height={bottomHeight}
                width={fullWidth}
                fill={`url(#${patternId})`}
                fillOpacity={0.6}
                x={xScale(-17 / 2 / 12)}
                y={yScale(botMax)}
                strokeWidth={0}
            />
            {/* 3B */}
            <rect
                height={height}
                width={defaultStrokeWidth}
                fill="#ccc"
                x={xScale(-17 / 2 / 12)}
                y={yScale(topMin)}
                strokeWidth={0}
            />

            {/* 1B */}
            <rect
                height={height}
                width={defaultStrokeWidth}
                fill="#ccc"
                x={xScale(17 / 2 / 12) - defaultStrokeWidth}
                y={yScale(topMin)}
                strokeWidth={0}
            />
        </g>
    )
}
